// Helpers for the ordered-submitters ("strict order") steps.
//
// A step used to hold exactly one submitter:
//   { step: 0, submitter: '12', entity_type: 'user' }
// Forms with "Specify submissions number" = Multiple let a step list several:
//   { step: 0, submitters: [{ submitter: '12', entity_type: 'user_group' }, ...] }
// Both shapes stay valid, so read a step through these rather than reaching for
// `submitter` / `entity_type` directly.

export const MIN_STRICT_ORDER_STEPS = 2;

// "Should form creator specify steps for the form?" - a strict-order form whose
// initiator picks the submitters can still have its steps set up by the creator:
// how many, the submitters of some, every step's "Notify user". The initiator
// fills the steps left empty and changes nothing else.
export const CREATOR_STEPS_KEY = 'should_form_creator_specify_steps';

// With "Specify submissions number" = Multiple the creator's steps also say which
// step's people each get a submission of their own - "The number of submissions is
// determined at step", a step index, Step 1 by default; every other step gets all of
// them - and, per step, "Create a folder for a form in Forms Assigned to Me" and "Only
// one submission is required from each group member": one member of each user group on
// the step submits, the users named on their own each (the API ignores it on the
// counting step, where each person fills their own form).
export const SUBMISSIONS_COUNT_STEP_KEY = 'submissions_count_step';
export const FORM_FOLDER_KEY = 'create_form_folder';
export const ONE_PER_GROUP_KEY = 'one_submission_per_group';

// The creator may also rename those steps; a step without a name of its own is "Step N".
export const STEP_NAME_KEY = 'step_name';
export const STEP_NAME_MAX_LENGTH = 100;

/** A step's name: the one the creator gave it, else "Step N" (`stepIndex` from 0). */
export const getStepName = (step, stepIndex) => step?.[STEP_NAME_KEY]?.trim() || `Step ${stepIndex + 1}`;

// "Hire date" next to a step at initiation: a month (1-12). Only the step's people hired
// in that month of an earlier year take part - the users it names and the members of its
// user groups alike. Any step may have it, the creator's locked ones too.
export const HIRE_MONTH_KEY = 'hire_month';

// The creator's steps of a multi-submission form decide, step by step, whether the
// initiator gets that "Hire date" button: "Show the Hire Date", not ticked by default -
// and only on a step left for the initiator to fill. Every other strict-order form has it
// on every step.
export const HIRE_DATE_FILTER_KEY = 'show_hire_date';

export const HIRE_MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: new Date(2000, index, 1).toLocaleString('en', { month: 'long' }),
}));

/** Whether `user` (an option with `hire_date`, "YYYY-MM-DD") was hired in `hireMonth` of a year before `year`. */
export const isHiredInMonth = (user, hireMonth, year = new Date().getFullYear()) => {
  // Read off the string: as a Date it would be midnight UTC, the day before west of it.
  const [hireYear, month] = String(user?.hire_date ?? '')
    .split('-')
    .map(Number);

  return !!hireYear && month === Number(hireMonth) && hireYear < year;
};

/**
 * Whether a step's submitter (a react-select option) takes part in a step filtered by
 * `hireMonth`: a user hired in it, a user group with a member hired in it - read off its
 * `member_hire_dates`; a user group not known to have any yet is kept.
 */
export const isOfHireMonth = (submitter, hireMonth) => {
  if (!hireMonth) {
    return true;
  }

  if (!isUserGroupSubmitter(submitter)) {
    return isHiredInMonth(submitter, hireMonth);
  }

  return !submitter.member_hire_dates || submitter.member_hire_dates.some((hire_date) => isHiredInMonth({ hire_date }, hireMonth));
};

/** The counting step once the step at `removedIndex` is gone: the same step while it is there, else Step 1. */
export const getCountStepAfterRemoving = (countStep, removedIndex) => {
  if (!countStep || countStep === removedIndex) {
    return 0;
  }

  return countStep > removedIndex ? countStep - 1 : countStep;
};

/** Whether the form's submission settings leave the steps to the creator (as the API decides it). */
export const hasCreatorSteps = (submissionSettings) =>
  submissionSettings?.[CREATOR_STEPS_KEY] === true &&
  submissionSettings?.filling_type === 'SPECIAL' &&
  submissionSettings?.submitters?.submitters_type === 'user' &&
  submissionSettings?.has_strict_order_of_submissions === true &&
  submissionSettings?.can_initiator_select_submitters === true &&
  !submissionSettings?.mandatory_save;

/** Whether the creator's steps also pick the step that sets the number of submissions, the folders, the hire date filter. */
export const hasMultipleSubmissionsCreatorSteps = (submissionSettings) =>
  hasCreatorSteps(submissionSettings) && submissionSettings?.only_one_submission === false;

/** Whether the initiator may filter the form's step `stepIndex` by hire date (HIRE_DATE_FILTER_KEY). */
export const canInitiatorFilterByHireDate = (submissionSettings, stepIndex) => {
  if (!hasMultipleSubmissionsCreatorSteps(submissionSettings)) {
    return true;
  }

  const step = submissionSettings.submitters_order?.[stepIndex];

  return step?.[HIRE_DATE_FILTER_KEY] === true && !getStepSubmitters(step).length;
};

/** The step's "Notify user" settings alone. */
export const getStepNotification = (step) => ({
  should_receive_notification: step?.should_receive_notification,
  notification_type: step?.notification_type,
  notification_text: step?.notification_text,
});

export const createEmptyStep = (stepNumber) => ({
  submitter: null,
  submitters: [],
  step: stepNumber,
  value: null,
  label: null,
  entity_type: null,
  public: false,
});

export const createInitialSteps = () => Array.from({ length: MIN_STRICT_ORDER_STEPS }, (_, index) => createEmptyStep(index));

// A form may carry `fixed_final_submitter` - a user from another company the API
// locks as the last step (see FIXED_FINAL_STRICT_ORDER_SUBMITTERS). No picker ever
// lists them, so they are placed here rather than chosen, in both step shapes at
// once so the step serialises whichever way the form needs.
export const toFixedFinalOption = (fixedFinalSubmitter) => ({
  value: fixedFinalSubmitter.id,
  label: fixedFinalSubmitter.label,
  entity_type: 'user',
});

/**
 * A step the API fixes rather than the initiator chooses, holding exactly
 * `options` (react-select options). Built in both step shapes at once, as above.
 */
export const createLockedStep = (options, stepNumber) => {
  const [first] = options;

  return {
    ...createEmptyStep(stepNumber),
    submitter: first?.value ?? null,
    value: first?.value ?? null,
    label: first?.label ?? null,
    entity_type: first?.entity_type ?? null,
    submitters: toStepSubmitters(options),
  };
};

/** Whether `step` holds exactly the submitters in `options`, and nobody else. */
export const isLockedStep = (step, options = []) => {
  const submitters = getStepSubmitters(step).map((item) => String(item.submitter));
  const expected = options.map((option) => String(option.value));

  return submitters.length === expected.length && expected.every((value) => submitters.includes(value));
};

export const createFixedFinalStep = (fixedFinalSubmitter, stepNumber) =>
  createLockedStep([toFixedFinalOption(fixedFinalSubmitter)], stepNumber);

/** Whether `step` is exactly the fixed user and nobody else. */
export const isFixedFinalStep = (step, fixedFinalSubmitter) => isLockedStep(step, [toFixedFinalOption(fixedFinalSubmitter)]);

/** Whether a step's submitter (or a react-select option) is a user group; anything else is a user. */
export const isUserGroupSubmitter = (submitter) => submitter?.entity_type === 'user_group';

/** Every submitter on a step, whichever shape it was stored in. */
export const getStepSubmitters = (step) => {
  if (!step) {
    return [];
  }

  if (step.submitters?.length) {
    return step.submitters;
  }

  if (step.submitter === null || step.submitter === undefined) {
    return [];
  }

  return [{ submitter: step.submitter, entity_type: step.entity_type, label: step.label, value: step.submitter }];
};

/** The step's submitters as react-select options, resolved against `options`. */
export const getStepSubmitterOptions = (step, options = []) =>
  getStepSubmitters(step).map((item) => {
    const matched = options.find((option) => String(option.value) === String(item.submitter));

    return (
      matched ?? {
        value: item.submitter,
        label: item.label ?? '',
        entity_type: item.entity_type,
      }
    );
  });

/** Turn a react-select selection back into the stored submitter list. */
export const toStepSubmitters = (selectedOptions = []) =>
  (selectedOptions ?? []).map(({ value, label, entity_type }) => ({
    submitter: value,
    entity_type,
    label,
    value,
  }));

/**
 * A step in the shape the API expects.
 *
 * A multi-submitter step sends `submitters` and must not also send the single
 * `submitter` / `entity_type` pair, which the API rejects as ambiguous.
 */
export const serializeSubmittersOrderStep = (step, index, allowMultipleSubmitters) => {
  const {
    // eslint-disable-next-line no-unused-vars
    submitters,
    submitter,
    entity_type,
    value,
    label,
    only_one_submission_required,
    [FORM_FOLDER_KEY]: hasFormFolder,
    [ONE_PER_GROUP_KEY]: isOnePerGroup,
    [STEP_NAME_KEY]: stepName,
    [HIRE_DATE_FILTER_KEY]: hasHireDateFilter,
    ...rest
  } = step ?? {};

  if (!allowMultipleSubmitters) {
    return { ...rest, submitter, entity_type, value, label, step: index };
  }

  const serialized = {
    ...rest,
    step: index,
    submitters: getStepSubmitters(step).map((item) => ({
      submitter: item.submitter,
      entity_type: item.entity_type,
    })),
  };

  // The first step is what generates the submissions, so the flag is not offered there.
  if (index > 0) {
    serialized.only_one_submission_required = !!only_one_submission_required;
  }

  if (hasFormFolder) {
    serialized[FORM_FOLDER_KEY] = true;
  }

  if (isOnePerGroup) {
    serialized[ONE_PER_GROUP_KEY] = true;
  }

  if (stepName?.trim()) {
    serialized[STEP_NAME_KEY] = stepName.trim();
  }

  if (hasHireDateFilter) {
    serialized[HIRE_DATE_FILTER_KEY] = true;
  }

  return serialized;
};
